import { orchestrator } from 'satcheljs';
import { setShowFolderPane } from '../actions/setShowFolderPane';
import {
    getIsBitSet,
    type ListViewBitFlagsMasks,
} from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import logFolderPaneState from '../helpers/logFolderPaneState';
import {
    isReadingPanePositionRight,
    isRPHiddenOrBottomByDefault as isRPHiddenOrBottomByDefaultFunc,
} from '../selectors/readingPanePosition';
import setCurrentClientReadingPanePosition from '../mutators/setCurrentClientReadingPanePositionMutator';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import { onClientReadingPanePositionChange } from '../actions/onClientReadingPanePositionChange';
import { getStore } from '../store/Store';
import {
    LayoutChangeSource,
    type BrowserWidthBucket,
    onAvailableWidthBucketChanged,
} from 'owa-layout';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { hasActiveDrawerPane } from 'owa-drawer';
import { isCapabilityEnabled } from 'owa-capabilities';
import { newHeaderCapability } from 'owa-capabilities-definitions/lib/newHeaderCapability';
import {
    isCopilotChatDrawerShown,
    shouldChatCollapseFolderPane,
} from 'owa-copilot-chat-drawer-store';

orchestrator(onAvailableWidthBucketChanged, actionMessage => {
    const { availableWidthBucket, source } = actionMessage;
    if (
        source == LayoutChangeSource.CalendarViewTypeChange ||
        source == LayoutChangeSource.DateRangeTypeChange
    ) {
        return;
    }
    if (availableWidthBucket <= 418) {
        collapseFolderPane(source);
        setClientReadingPanePositionOff();
        return;
    }

    /**
     * When user has chosen reading pane setting to be hidden/bottom (also called as single line view),
     * we want to collapse the left nav as late as possible
     * as there are only two columns (LeftNav, LV/RP) to share the space. This is so as to continue offering
     * the default layout of the user as much as we can.
     */
    const isRPHiddenORBottomByDefault = isRPHiddenOrBottomByDefaultFunc();
    if (isRPHiddenORBottomByDefault && availableWidthBucket <= 541) {
        collapseFolderPane(source);
        setClientReadingPanePositionOff();
        return;
    }

    /**
     * When user has chosen reading pane setting to be right (also called as 3 column view)
     */
    if (!isRPHiddenORBottomByDefault) {
        if (availableWidthBucket <= 768) {
            collapseFolderPane(source);
            setClientReadingPanePositionOff();
            return;
        }

        // Fix overlap in the mid-width range when using a resizable drawer pane.
        // In that mode, the right pane can grow significantly while this orchestrator still uses
        // coarse width buckets; collapsing the folder pane at a slightly wider bucket prevents the
        // combined minimum widths (folder pane + list + reading pane + drawer) from overcommitting
        // the available space and visually covering mail content.
        const folderPaneCollapseThreshold =
            isCapabilityEnabled(newHeaderCapability) && hasActiveDrawerPane(window) ? 1079 : 918;

        /**
         * We want to collapse the left nav more aggressively
         * as there are three columns (LeftNav, LV and RP) to share the space
         */
        if (availableWidthBucket <= folderPaneCollapseThreshold) {
            collapseFolderPane(source);
            resetClientReadingPanePosition();
            return;
        }
    }

    // Copilot Chat Drawer being opened should keep the folder pane from expanding
    // automatically due to width.
    const keepFolderPaneClosedForCopilotChatDrawer =
        shouldChatCollapseFolderPane() && isCopilotChatDrawerShown();

    if (!keepFolderPaneClosedForCopilotChatDrawer) {
        expandFolderPane(source);
    }

    // Always reset reading pane position when there is enough width, even if the
    // folder pane is kept collapsed for the Copilot Chat Drawer. Without this,
    // collapsing the drawer doesn't restore the message list / reading pane.
    resetClientReadingPanePosition();
});

/**
 * Force hide folder pane as determined by the current browser width bucket
 * @param source the layout change source
 */
function collapseFolderPane(source: LayoutChangeSource) {
    if (getStore().showFolderPane) {
        setShowFolderPane(false);
        logFolderPaneState(
            source,
            false /* folderPaneNewShowState */,
            true /* folderPaneOldShowState */
        );
    }
}

/**
 * Try expand folder pane as determined by the current browser width bucket
 * @param source the layout change source
 */
function expandFolderPane(source: LayoutChangeSource) {
    const showFolderPane = !getIsBitSet(4, getGlobalSettingsAccountMailboxInfo());

    if (showFolderPane && !getStore().showFolderPane) {
        setShowFolderPane(true);
        logFolderPaneState(
            source,
            true /* folderPaneNewShowState */,
            false /* folderPaneOldShowState */
        );
    }
}

/**
 * Sets the client reading pane position best suited for the current browser layout
 */
function setClientReadingPanePositionOff() {
    // Change RP position to Off if it is Right
    const currentReadingPanePositionRight = isReadingPanePositionRight();
    if (!currentReadingPanePositionRight) {
        return;
    }

    setCurrentClientReadingPanePosition(ReadingPanePosition.Off);
    onClientReadingPanePositionChange();
}

/**
 * Resets the client reading pane position
 */
function resetClientReadingPanePosition() {
    setCurrentClientReadingPanePosition(null);
    onClientReadingPanePositionChange();
}
